import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import type { Anrede, Rolle, Rollenbezug } from "@dj/domain/gemeinsam";
import { Ansicht } from "../design/Ansicht";
import { Dialog } from "../design/Dialog";
import { Klappliste, Textfeld } from "../design/Formular";
import { Abweisung } from "../design/Hinweis";
import { Knopf } from "../design/Kachel";
import { EigeneRolle } from "../design/Person";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { IphoneHinweis } from "../plattform/IphoneHinweis";
import { OfflineBereit, OfflineHinweis } from "../plattform/Offline";
import { useSammlung } from "../plattform/lesemodelle";
import { useSitzung } from "../plattform/sitzung";
import { Beteiligungseinladungen } from "./Beteiligungseinladungen";

/**
 * Meine Drückjagden (oberflaeche.md §7, lesemodelle.md §6): je Drückjagd ein `article` mit `link` „<Bezeichnung>“,
 * Revier und eigener Rolle nach eigener Anrede (ADR-0025); Jagdherren legen hier eine Drückjagd an (Dialog „Drückjagd“).
 * Vor der Bekanntgabe kennt ein Mitwirkender ohne leitende Rolle nur Jagddatum und Veranstalter (F-062).
 * `vorne`: erster Eintrag der Liste aus einem anderen Kontext (die Bitte um einen Wiederherstellungskontakt, App.tsx).
 * Einladungen zu gemeinsamen Drückjagden stehen davor als eigene `region` (V-24).
 */
export function MeineDrueckjagden({ vorne }: { vorne?: ReactNode } = {}) {
  const { uid } = useSitzung();
  const liste = useSammlung(uid ? `personen/${uid}/meine_drueckjagden` : null);
  const reviere = useSammlung(uid ? `personen/${uid}/reviere` : null);
  const [offen, setOffen] = useState(false);
  const zeilen = [...liste.daten].sort((a, b) => String(a["Jagddatum"] ?? "").localeCompare(String(b["Jagddatum"] ?? "")));
  // Referenz `#s-drueckjagden`: je Drückjagd ein Eintrag mit dem Link als Querkachel, Revier, Jagddatum und eigener Rolle
  // darunter, am Jagdtag „Offline bereit“; „Anlegen“ fest unten (Jagdherr).
  return (
    <Ansicht name="Meine Drückjagden" symbol="drueckjagden"
      unten={reviere.daten.length ? <Knopf art="planung" symbol="hinzufuegen" onPress={() => setOffen(true)}>Anlegen</Knopf> : undefined}>
      {vorne}
      <Beteiligungseinladungen />
      <OfflineHinweis />
      <IphoneHinweis />
      {zeilen.length === 0 && !liste.laedt ? <p className="leer platte"><Symbol name="drueckjagden" /><span>Noch keine Drückjagd</span></p> : null}
      {zeilen.map((d) => {
        const titel = d["Drückjagd"] ? String(d["Drückjagd"]) : `Drückjagd am ${datumText(d["Jagddatum"])}`;
        const rollen = (d["Rollen"] as Rolle[] | undefined) ?? [];
        const anrede = (d["_anrede"] as Anrede | undefined) ?? "ohne Anrede";
        return (
          <article key={d._id} className="eintrag platte" aria-labelledby={`dj-${d._id}`}>
            <Link to={`/drueckjagd/${d._id}`} className="k k--quer" aria-label={titel}>
              <Symbol name="drueckjagden" />
              <span className="k__b" id={`dj-${d._id}`}>{titel}</span>
            </Link>
            <span className="eintrag__meta">{[d["Revier"] ? String(d["Revier"]) : null, datumText(d["Jagddatum"]), d["Drückjagdstatus"] ? String(d["Drückjagdstatus"]) : null].filter(Boolean).join(" · ")}</span>
            <span className="eintrag__meta"><EigeneRolle anrede={anrede} rollen={rollen} bezug={d["_bezug"] as Rollenbezug | undefined} /></span>
            <OfflineBereit drueckjagdId={d._id} />
          </article>
        );
      })}
      <AnlegenDialog offen={offen} reviere={reviere.daten.map((r) => ({ wert: r._id, text: String(r["Revier"]) }))} onSchliessen={() => setOffen(false)} />
    </Ansicht>
  );
}

/** Datum für die Anzeige außerhalb von Tabellenzellen (design/README §11: `14.11.2026`). */
function datumText(w: unknown): string {
  if (w instanceof Date) return w.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(w ?? ""));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(w ?? "");
}

export function AnlegenDialog({ offen, reviere, onSchliessen }: { offen: boolean; reviere: Array<{ wert: string; text: string }>; onSchliessen: () => void }) {
  const navigate = useNavigate();
  const [revier, setRevier] = useState<string | null>(null);
  const [felder, setFelder] = useState({ Bezeichnung: "", Jagddatum: "", Treffpunkt: "" });
  const [grund, setGrund] = useState<string | null>(null);
  const gewaehlt = revier ?? (reviere.length === 1 ? reviere[0]!.wert : null);
  async function speichern() {
    setGrund(null);
    const daten: Record<string, unknown> = { Revier: gewaehlt };
    for (const [k, v] of Object.entries(felder)) if (v.trim()) daten[k] = v.trim();
    const a = await befehl("DrueckjagdAnlegen", daten);
    if (!a.ok) { setGrund(abweisungsgrund(a)); return; }
    setFelder({ Bezeichnung: "", Jagddatum: "", Treffpunkt: "" });
    onSchliessen();
    if (a.kennungen["Drueckjagd"]) navigate(`/drueckjagd/${a.kennungen["Drueckjagd"]}/planung`);
  }
  return (
    <Dialog name="Drückjagd" symbol="drueckjagden" offen={offen} onSchliessen={onSchliessen}>
      <div className="formular">
        <Klappliste label="Revier" wert={gewaehlt} leer="bitte wählen" optionen={reviere} onChange={setRevier} />
        <Textfeld label="Bezeichnung" wert={felder.Bezeichnung} onChange={(v) => setFelder({ ...felder, Bezeichnung: v })} />
        <Textfeld label="Jagddatum" wert={felder.Jagddatum} onChange={(v) => setFelder({ ...felder, Jagddatum: v })} typ="date" />
        <Textfeld label="Treffpunkt" wert={felder.Treffpunkt} onChange={(v) => setFelder({ ...felder, Treffpunkt: v })} />
        <Abweisung grund={grund} />
        <div className="knopfreihe">
          <Knopf onPress={speichern}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}
